// src/components/SetupInfoPanel.js
import React from 'react';
import { INITIAL_CELLS } from '../constants/gameConstants';

// CSS animasyonları için (eğer global CSS'iniz yoksa)
const styles = `
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fadeIn {
  animation: fadeIn 0.5s ease-out;
}
`;

export const SetupInfoPanel = ({ gameState, players, myPlayerIndex, selectedSetupCells }) => {
    // Setup aşaması değilse gösterme (null yerine boş fragment döndür)
    if (gameState.turn !== 0) return null;

    const currentPlayer = players[gameState.currentPlayerIndex];
    const isMyTurn = gameState.currentPlayerIndex === myPlayerIndex;
    
    // Her oyuncunun seçim durumunu hesapla
    const playerSelections = players.map((player, idx) => {
        const cellCount = gameState.grid.filter(cell => cell === idx).length;
        return {
            playerName: player.name,
            cellCount: cellCount,
            isComplete: cellCount === INITIAL_CELLS,
            color: player.color,
            isBot: player.isBot,
            isMe: idx === myPlayerIndex
        };
    });

    const totalSelected = gameState.grid.filter(c => c >= 0).length;
    const totalNeeded = players.length * INITIAL_CELLS;
    const currentPlayerCells = playerSelections[gameState.currentPlayerIndex].cellCount;
    const progressPercentage = (totalSelected / totalNeeded) * 100;

    return (
        <div className="h-full flex flex-col gap-3 overflow-y-auto">
            {/* Başlık ve İlerleme */}
            <div className="bg-gradient-to-r from-purple-900 to-indigo-900 rounded-lg p-3 border-2 border-purple-400 flex-shrink-0">
                <div className="flex items-center justify-between mb-2">
                    <h2 className="text-sm font-bold text-purple-200 flex items-center gap-1">
                        <span>🗺️</span>
                        <span>Bölge Seçimi</span>
                    </h2>
                    <div className="text-purple-300 font-mono text-xs">
                        {totalSelected}/{totalNeeded}
                    </div>
                </div>
                
                {/* İlerleme Çubuğu */}
                <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                    <div 
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-full transition-all duration-500 rounded-full"
                        style={{ width: `${progressPercentage}%` }}
                    />
                </div>
            </div>

            {/* Sıra Durumu */}
            <div className="flex-shrink-0">
                {isMyTurn ? (
                    <div className="bg-green-500 bg-opacity-20 border-2 border-green-400 rounded-lg p-3 animate-pulse">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-2xl">🎯</span>
                            <h3 className="text-sm font-bold text-green-300">SENİN SIRAN!</h3>
                        </div>
                        <p className="text-xs text-green-200 mb-2">
                            {INITIAL_CELLS - selectedSetupCells.length} kare seç
                        </p>
                        {/* Seçim Göstergesi */}
                        <div className="flex gap-1">
                            {Array.from({ length: INITIAL_CELLS }).map((_, i) => (
                                <div 
                                    key={i}
                                    className={`flex-1 h-6 rounded border-2 transition-all ${
                                        i < selectedSetupCells.length 
                                            ? 'bg-green-500 border-green-300' 
                                            : 'bg-gray-700 border-gray-500'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="bg-gray-800 bg-opacity-50 border-2 border-gray-600 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                            <div 
                                className="w-10 h-10 rounded-full animate-pulse flex-shrink-0"
                                style={{ backgroundColor: currentPlayer.color }}
                            />
                            <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-bold text-gray-200 truncate">
                                    {currentPlayer.name}
                                    {currentPlayer.isBot && ' 🤖'}
                                </h3>
                                <p className="text-xs text-gray-400">
                                    {currentPlayerCells}/{INITIAL_CELLS} seçildi
                                </p>
                            </div>
                        </div>
                        {/* İlerleme Göstergesi */}
                        <div className="flex gap-1">
                            {Array.from({ length: INITIAL_CELLS }).map((_, i) => (
                                <div 
                                    key={i}
                                    className={`flex-1 h-5 rounded border-2 transition-all ${
                                        i < currentPlayerCells
                                            ? 'border-yellow-300'
                                            : 'bg-gray-700 border-gray-500'
                                    }`}
                                    style={i < currentPlayerCells ? {
                                        backgroundColor: currentPlayer.color
                                    } : {}}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Oyuncu Durumları */}
            <div className="flex-1 overflow-y-auto">
                <h3 className="text-xs font-bold text-blue-300 mb-2 flex items-center gap-1 sticky top-0 bg-black bg-opacity-60 p-2 rounded">
                    <span>📋</span>
                    Oyuncu Durumu
                </h3>
                <div className="space-y-2 px-1">
                    {playerSelections.map((selection, idx) => (
                        <div 
                            key={idx}
                            className={`flex items-center gap-2 p-2 rounded-lg border-2 transition-all ${
                                selection.isComplete 
                                    ? 'bg-green-900 bg-opacity-30 border-green-500' 
                                    : idx === gameState.currentPlayerIndex
                                    ? 'bg-yellow-900 bg-opacity-30 border-yellow-500 animate-pulse'
                                    : 'bg-gray-800 bg-opacity-30 border-gray-600'
                            }`}
                        >
                            <div 
                                className="w-8 h-8 rounded-full border-2 border-white flex-shrink-0"
                                style={{ backgroundColor: selection.color }}
                            />
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-semibold truncate">
                                    {selection.playerName}
                                    {selection.isBot && ' 🤖'}
                                    {selection.isMe && ' (Sen)'}
                                </div>
                                <div className="flex items-center gap-1 mt-0.5">
                                    {selection.isComplete ? (
                                        <span className="text-xs text-green-400 font-bold">✓ Hazır</span>
                                    ) : (
                                        <>
                                            <span className="text-xs text-gray-400">
                                                {selection.cellCount}/{INITIAL_CELLS}
                                            </span>
                                            <div className="flex gap-0.5 ml-1">
                                                {Array.from({ length: INITIAL_CELLS }).map((_, i) => (
                                                    <div 
                                                        key={i}
                                                        className={`w-2 h-2 rounded-sm ${
                                                            i < selection.cellCount ? 'bg-blue-400' : 'bg-gray-600'
                                                        }`}
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* İpucu */}
            <div className="bg-blue-900 bg-opacity-30 rounded-lg p-2 border border-blue-400 border-opacity-30 flex-shrink-0">
                <h3 className="text-xs font-bold text-blue-300 mb-1 flex items-center gap-1">
                    <span>💡</span>
                    İpucu
                </h3>
                <p className="text-xs text-gray-300 leading-tight">
                    Birbirine yakın kareler seç! Tüm seçimler bitince savaş başlar.
                </p>
            </div>
        </div>
    );
};